import React, { useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from "react-simple-maps";
import { scaleLinear } from "d3-scale";

// Free open-source TopoJSON for the world map
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Dictionary mapping string locations to actual map coordinates [Longitude, Latitude]
const locationCoordinates = {
  "Cyberjaya": [101.6572, 2.9228],
  "Kuala Lumpur": [101.6869, 3.1390],
  "Shah Alam": [101.5264, 3.0738],
  "Petaling Jaya": [101.6067, 3.1073],
  "Subang Jaya": [101.5859, 3.0438],
  "Puchong": [101.5833, 3.0333],
  "Klang": [101.4482, 3.0333],
  "Johor Bahru": [103.7618, 1.4927],
  "George Town": [100.3288, 5.4141],
  "Ipoh": [101.0901, 4.5975],
  "Melaka": [102.2512, 2.1896],
  "Kuching": [110.3592, 1.5533],
  "Kota Kinabalu": [110.3382, 1.5535],
  "Singapore": [103.8198, 1.3521],
  "Jakarta": [106.8456, -6.2088],
  "MY": [101.9758, 4.2105],
  "SG": [103.8198, 1.3521],
  "ID": [113.9213, -0.7893],
  "US": [-95.7129, 37.0902],
  "GB": [-3.4359, 55.3781],
  "UK": [-3.4359, 55.3781],
  "AU": [133.7751, -25.2744],
};

const LocationMap = ({ data, isDarkMode, theme, regionFilter = 'all' }) => {
  const [tooltip, setTooltip] = useState(null);

  // Process data to group by city/country, attach coordinates and sources
  const markerData = useMemo(() => {
    const counts = {};
    data.forEach((view) => {
      let locName = null;
      let coords = null;

      if (view.city && view.city !== "Unknown" && locationCoordinates[view.city]) {
        locName = view.city;
        coords = locationCoordinates[view.city];
      } else if (view.country && view.country !== "Unknown" && locationCoordinates[view.country]) {
        locName = view.country;
        coords = locationCoordinates[view.country];
      }

      if (locName && coords) {
        if (!counts[locName]) {
          counts[locName] = { name: locName, coordinates: coords, count: 0, sources: {} };
        }
        counts[locName].count += 1;
        const source = view.source || "other";
        counts[locName].sources[source] = (counts[locName].sources[source] || 0) + 1;
      }
    });
    
    return Object.values(counts).map(loc => {
      const dominant = Object.entries(loc.sources).sort((a, b) => b[1] - a[1])[0][0];
      return { ...loc, dominantSource: dominant };
    });
  }, [data]);

  const maxValue = Math.max(...markerData.map(m => m.count), 1);
  const sizeScale = scaleLinear().domain([1, maxValue]).range([4, 20]);

  // Dynamically change map focus based on the selected region toggle
  const mapConfig = useMemo(() => {
    if (regionFilter === 'my') return { center: [101.65, 4.21], zoom: 5 }; // Ultra-zoomed on Malaysia
    if (regionFilter === 'intl') return { center: [15, 20], zoom: 1 };     // Zoomed out to global view
    return { center: [101.65, 4.21], zoom: 3 };                            // Default SE Asia focus
  }, [regionFilter]);

  return (
    <div className="w-full flex flex-col relative" style={{ touchAction: "pan-y" }}>
      <div className={`text-[8px] sm:hidden font-bold uppercase tracking-widest opacity-40 text-center mb-3 ${theme.text}`}>
        Pinch to zoom, drag to pan
      </div>
      
      <div className="w-full h-[250px] sm:h-[400px] overflow-hidden rounded-2xl bg-stone-50/50 dark:bg-stone-900/50 border border-stone-100 dark:border-stone-800">
        <ComposableMap
          projectionConfig={{ scale: 140 }}
          width={800}
          height={400}
          style={{ width: "100%", height: "100%" }}
        >
          <ZoomableGroup key={regionFilter} center={mapConfig.center} zoom={mapConfig.zoom} minZoom={1} maxZoom={8}>
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={isDarkMode ? "#1c1917" : "#e7e5e4"}
                    stroke={isDarkMode ? "#292524" : "#ffffff"}
                    strokeWidth={0.5}
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none" },
                      pressed: { outline: "none" },
                    }}
                  />
                ))
              }
            </Geographies>

            {markerData.map(({ name, coordinates, count, dominantSource, sources }) => {
              const radius = sizeScale(count);
              const bubbleColor = dominantSource === 'whatsapp' ? '#10b981' : dominantSource === 'direct' ? '#3b82f6' : '#8b5cf6';
              
              return (
                <Marker key={name} coordinates={coordinates}>
                  <circle 
                    r={radius} 
                    fill={bubbleColor} 
                    opacity={0.4} 
                    className="cursor-pointer hover:opacity-70 transition-opacity"
                    onMouseEnter={(e) => setTooltip({ name, count, sources, x: e.clientX, y: e.clientY })}
                    onMouseMove={(e) => setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null)}
                    onMouseLeave={() => setTooltip(null)}
                  />
                  <circle r={radius * 0.5} fill={bubbleColor} className="pointer-events-none" />
                  <text
                    textAnchor="middle"
                    y={-radius - 2}
                    style={{ fontFamily: "system-ui", fill: isDarkMode ? "#a8a29e" : "#57534e", fontSize: "4px", fontWeight: "bold", pointerEvents: "none" }}
                  >
                    {name} ({count})
                  </text>
                </Marker>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>
      </div>
      
      <div className="flex flex-wrap items-center justify-end gap-4 mt-4 text-[8px] font-bold uppercase tracking-widest opacity-80">
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-[#10b981]" /><span className={theme.text}>WhatsApp</span></div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-[#3b82f6]" /><span className={theme.text}>Direct</span></div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-[#8b5cf6]" /><span className={theme.text}>Other</span></div>
      </div>

      {/* Tooltip Portal */}
      {tooltip && (
        <div 
          className={`fixed z-[100] pointer-events-none px-4 py-3 rounded-xl shadow-2xl border text-xs min-w-[140px] transition-opacity duration-150 ${isDarkMode ? 'bg-stone-900 border-stone-800 text-stone-200' : 'bg-white border-stone-100 text-stone-800'}`}
          style={{ left: tooltip.x + 15, top: tooltip.y + 15 }}
        >
          <div className="font-serif font-bold text-sm mb-1">{tooltip.name}</div>
          <div className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-3">{tooltip.count} Total Views</div>
          <div className="flex flex-col gap-1.5 text-[9px] font-bold uppercase tracking-widest pt-3 border-t border-stone-500/20">
             {tooltip.sources.whatsapp ? <div className="flex justify-between gap-4"><span className="text-[#10b981]">WhatsApp</span><span>{tooltip.sources.whatsapp}</span></div> : null}
             {tooltip.sources.direct ? <div className="flex justify-between gap-4"><span className="text-[#3b82f6]">Direct</span><span>{tooltip.sources.direct}</span></div> : null}
             {tooltip.sources.other ? <div className="flex justify-between gap-4"><span className="text-[#8b5cf6]">Other</span><span>{tooltip.sources.other}</span></div> : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationMap;